// Master Apps · exportação de CSV pensada para o Excel em pt-BR.
//
// Sem dependência externa: monta o texto, entrega via Blob + objectURL e
// libera a URL depois. Os detalhes que costumam quebrar o arquivo no Excel
// brasileiro já vêm resolvidos aqui:
//
//  · separador `;` (o Excel pt-BR usa o separador de listas do Windows, não a
//    vírgula) e fim de linha CRLF;
//  · BOM UTF-8 no início, senão os acentos viram "Ã§" ao abrir com duplo
//    clique;
//  · aspas duplas escapadas dobrando-as, e qualquer valor com `;`, aspas,
//    quebra de linha ou espaço nas pontas sai entre aspas;
//  · booleano vira "Sim" / "Não"; `null` / `undefined` viram célula vazia;
//  · decimal com vírgula (e sem separador de milhar, para o Excel continuar
//    lendo como número);
//  · célula que começaria com `=`, `+`, `@`, TAB ou CR ganha apóstrofo, para o
//    Excel não executar o conteúdo como fórmula.
//
// Datas: passe já formatadas em DD/MM/AAAA com `formatDateBR` (lib/format.ts).

export type CsvValue = string | number | boolean | null | undefined;

/** Uma linha do CSV: chave = cabeçalho da coluna, valor = célula. */
export type CsvRow = Record<string, CsvValue>;

const SEPARATOR = ';';
const EOL = '\r\n';
const BOM = '\ufeff';

// O revoke não pode acontecer no mesmo tick do clique: alguns navegadores
// cancelam o download se a URL do blob morre antes de eles começarem a ler.
const REVOKE_DELAY_MS = 1000;

// Início de célula que o Excel interpretaria como fórmula.
const FORMULA_START = /^[=+@\t\r]/;

function formatValue(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    return Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
  }
  return value;
}

function escapeCell(value: CsvValue): string {
  let cell = formatValue(value);
  if (FORMULA_START.test(cell)) cell = `'${cell}`;
  const needsQuotes =
    cell.includes(SEPARATOR) ||
    cell.includes('"') ||
    cell.includes('\n') ||
    cell.includes('\r') ||
    cell !== cell.trim();
  return needsQuotes ? `"${cell.replaceAll('"', '""')}"` : cell;
}

/** Cabeçalhos na ordem em que aparecem nas linhas (primeira ocorrência). */
function collectHeaders(rows: readonly CsvRow[]): string[] {
  const headers: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!headers.includes(key)) headers.push(key);
    }
  }
  return headers;
}

/**
 * Texto completo do CSV (já com BOM). Útil para testar o conteúdo sem tocar no
 * DOM. Informe `headers` para fixar a ordem das colunas — sem isso a ordem sai
 * das chaves das linhas.
 */
export function buildCsv(rows: readonly CsvRow[], headers?: readonly string[]): string {
  const columns = headers ?? collectHeaders(rows);
  const lines = [columns.map(escapeCell).join(SEPARATOR)];
  for (const row of rows) {
    lines.push(columns.map((column) => escapeCell(row[column])).join(SEPARATOR));
  }
  return BOM + lines.join(EOL) + EOL;
}

/**
 * Baixa as linhas como CSV. Devolve `false` quando não havia nada para
 * exportar (nenhuma coluna), para a tela poder avisar em vez de gerar um
 * arquivo vazio. A extensão `.csv` é acrescentada se faltar.
 */
export function downloadCsv(
  filename: string,
  rows: readonly CsvRow[],
  headers?: readonly string[],
): boolean {
  const columns = headers ?? collectHeaders(rows);
  if (columns.length === 0) return false;

  const blob = new Blob([buildCsv(rows, columns)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.toLowerCase().endsWith('.csv') ? filename : `${filename}.csv`;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  return true;
}

/** Nome de arquivo com a data do dia: `inventario-2026-08-11.csv`. */
export function csvFilename(prefix: string, at: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${prefix}-${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}.csv`;
}
